/**
 * The bid's questions joined with this document: mytender's list and status,
 * which questions have an answer block here, and the sync operations the
 * Tender tab, the question sheet, the push sheet and io.ai's tools share.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { loadAppliedTemplate } from "../document/identity";
import { contentHash } from "../document/markdown";
import {
  type BlockContent,
  type PullItem,
  type QuestionRef,
  listBlocks,
  pullIntoDocument,
  pullManyIntoDocument,
  questionRanges,
  readBlocks,
  type AnswerPlacement,
  type PullStyleChoice,
  resolvePullStyles,
} from "../document/questionBlocks";
import { pushBase, readSyncRecord, recordSync } from "../document/syncRecord";
import {
  type LiveAnswer,
  type PushResult,
  type Question,
  type SectionStatus,
  fetchLive,
  fetchQuestions,
  pushQuestions,
  type ReviewRequest,
  saveLimit,
  saveWritingPlan,
  sendForReview as sendForReviewApi,
  setQuestionStatus,
} from "./questionsApi";
import { describeRequestError } from "./requestError";

export const questionRef = (q: Question): QuestionRef => {
  const depth = (q.section_number || "").split(".").filter(Boolean).length;
  return {
    sectionId: q.section_id,
    number: q.section_number,
    heading: q.heading || q.question.slice(0, 80),
    level: depth >= 3 ? 3 : depth === 2 ? 2 : 1,
  };
};

/** Where a pull is: fetching the answers, writing them, or checking what landed. */
export type PullProgress = {
  stage: "fetching" | "writing" | "finishing";
  /** Progress out of `total`, for the bar. */
  done: number;
  total: number;
  /** Questions written so far (all of them once the write lands). */
  doneIds: string[];
};

/** Answers fetched per request (the backend's cap). */
const LIVE_BATCH = 80;

/** What the agent decided for a pull: where each new answer goes and in which styles. */
export type PullDirectives = {
  placements?: (AnswerPlacement & { section_id: string })[];
  styles?: PullStyleChoice | null;
};

export type PullOutcomeRow = {
  sectionId: string;
  status: "replaced" | "added" | "unchanged" | "empty" | "error";
  message?: string;
};

/**
 * unchanged: neither side changed since the last pull or push. behind: only
 * mytender changed (pushing would overwrite it with an older copy). changed:
 * only the document changed. newer: both changed.
 */
export type Verdict = "unchanged" | "behind" | "changed" | "newer" | "new" | "skipped" | "missing";

export type PushPlanRow = {
  question: Question;
  verdict: Verdict;
  content: BlockContent | null;
  live: LiveAnswer | null;
  /** Checked rows are pushed. */
  include: boolean;
};

export type PushPlan = { rows: PushPlanRow[]; lastPushAt: string | null; stale: boolean };

const STALE_DAYS = 3;

/** Shallow equality on a question record: the server's row vs the pane's. */
const sameRecord = (a: Question, b: Question): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const x = (a as Record<string, unknown>)[key];
    const y = (b as Record<string, unknown>)[key];
    if (x === y) continue;
    if (typeof x === "object" && typeof y === "object" && JSON.stringify(x) === JSON.stringify(y)) continue;
    return false;
  }
  return true;
};

export const useQuestions = (bidId: string | null, token: string | null) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [blocks, setBlocks] = useState<Map<string, { words: number }>>(new Map());
  const [otherBidId, setOtherBidId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // How many answers in the document differ from what mytender last saw
  // (got or sent), for the pane's send nudge. A real comparison of the
  // document against the sync record, not a count of edits: a change the
  // writer has since removed or rejected no longer counts.
  const [changedSinceSend, setChangedSinceSend] = useState(0);
  const recountChanged = useCallback(async () => {
    if (!bidId) {
      setChangedSinceSend(0);
      return;
    }
    try {
      const contents = await readBlocks(bidId);
      const record = readSyncRecord(bidId);
      let changed = 0;
      for (const [sectionId, content] of contents) {
        const markdown = content.markdown.trim();
        if (!markdown) continue;
        const last = record?.questions[sectionId];
        if (!last) {
          changed += 1; // written here, never synced
          continue;
        }
        const docHash = await contentHash(content.markdown);
        if (docHash !== (last.docHash ?? last.hash)) changed += 1;
      }
      setChangedSinceSend(changed);
    } catch {
      // Word unavailable (browser preview); leave as is.
    }
  }, [bidId]);

  const refreshBlocks = useCallback(async () => {
    if (!bidId) return;
    try {
      const state = await listBlocks(bidId);
      setBlocks(new Map([...state.blocks].map(([k, v]) => [k, { words: v.words }])));
      setOtherBidId(state.otherBidId);
    } catch {
      // Word unavailable (browser preview); leave as is.
    }
  }, [bidId]);

  const refresh = useCallback(async () => {
    if (!bidId || !token) {
      setQuestions([]);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const [list] = await Promise.all([fetchQuestions(bidId, token, controller.signal), refreshBlocks()]);
      if (!controller.signal.aborted) setQuestions(list);
    } catch (err) {
      if (!controller.signal.aborted) setError(describeRequestError(err));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [bidId, token, refreshBlocks]);

  useEffect(() => {
    void refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  const patch = (sectionId: string, update: Partial<Question>) =>
    setQuestions((qs) => qs.map((q) => (q.section_id === sectionId ? { ...q, ...update } : q)));

  // Writes in flight (a push, a status change, a saved plan or limit): a
  // background sync must not race them with an older reading of the server.
  const mutating = useRef(0);
  const tracked = async <T>(work: () => Promise<T>): Promise<T> => {
    mutating.current += 1;
    try {
      return await work();
    } finally {
      mutating.current -= 1;
    }
  };

  /**
   * Bring the list up to date with mytender without disturbing the pane:
   * rows whose fields changed (a limit edited on the platform, a status set
   * by a reviewer) are swapped for the server's, unchanged rows keep their
   * identity, nothing is aborted, no loading state, no error banner. Called
   * on the Tender tab's timer, so a limit changed in the web app shows in
   * Word within a few seconds without switching tabs.
   */
  const syncFromServer = useCallback(async () => {
    if (!bidId || !token || mutating.current > 0) return;
    let list: Question[];
    try {
      list = await fetchQuestions(bidId, token);
    } catch {
      return; // a background tick: silent, the last good list stands
    }
    if (mutating.current > 0) return;
    setQuestions((prev) => {
      const sameShape = list.length === prev.length && list.every((q, i) => q.section_id === prev[i]?.section_id);
      if (!sameShape) return list;
      let changed = false;
      const next = prev.map((q, i) => {
        const fresh = list[i];
        if (sameRecord(q, fresh)) return q;
        changed = true;
        return fresh;
      });
      return changed ? next : prev;
    });
  }, [bidId, token]);

  const pullNow = useCallback(
    async (
      sectionIds?: string[],
      onProgress?: (p: PullProgress) => void,
      directives: PullDirectives = {}
    ): Promise<PullOutcomeRow[]> => {
      if (!bidId || !token) return [];
      const placementOf = new Map((directives.placements ?? []).map((p) => [p.section_id, p]));
      const applied = await loadAppliedTemplate().catch(() => null);
      const styles = resolvePullStyles(directives.styles, applied?.styleMap);
      const targets = questions.filter((q) => !sectionIds?.length || sectionIds.includes(q.section_id));
      const ids = targets.map((q) => q.section_id);
      const total = targets.length;
      const report = (stage: PullProgress["stage"], doneIds: string[] = []) =>
        onProgress?.({
          stage,
          done: stage === "fetching" ? 0 : stage === "writing" ? Math.ceil(total / 2) : total,
          total,
          doneIds,
        });
      report("fetching");

      // Every answer in one request (the list's copy if it fails), and what
      // the document holds now, read while the request is out.
      const live = new Map<string, LiveAnswer>();
      const fetching = (async () => {
        for (let i = 0; i < ids.length; i += LIVE_BATCH) {
          try {
            for (const l of await fetchLive(bidId, ids.slice(i, i + LIVE_BATCH), token)) live.set(l.section_id, l);
          } catch {
            // the list's copy stands in
          }
        }
      })();
      const [presentState, current] = await Promise.all([
        listBlocks(bidId).catch(() => null),
        readBlocks(bidId, ids).catch(() => new Map<string, { markdown: string }>()),
        fetching,
      ]);
      const present = presentState?.blocks ?? new Map();
      const record = readSyncRecord(bidId);

      const outcomes: PullOutcomeRow[] = [];
      const items: PullItem[] = [];
      const hashes = new Map<string, string>();
      const pulled = new Map<string, string>();
      for (const q of targets) {
        const l = live.get(q.section_id);
        const answer = typeof l?.answer_markdown === "string" ? l.answer_markdown : q.answer_markdown;
        const hash = l?.answer_hash ?? q.answer_hash;
        hashes.set(q.section_id, hash);
        pulled.set(q.section_id, answer);
        if (!answer.trim() && present.has(q.section_id)) {
          outcomes.push({ sectionId: q.section_id, status: "empty" });
          continue;
        }
        const inDocument = current.get(q.section_id)?.markdown;
        if (inDocument?.trim() && answer.trim()) {
          // Up to date when the document's answer is what the last pull or
          // push left (as Word reads it back) and mytender's hasn't moved
          // since; a document with no record is compared directly.
          const docHash = await contentHash(inDocument);
          const last = record.questions[q.section_id];
          const upToDate = last?.docHash ? last.docHash === docHash && last.hash === hash : docHash === hash;
          if (upToDate) {
            outcomes.push({ sectionId: q.section_id, status: "unchanged" });
            continue;
          }
        }
        // Content where there is none yet, a tracked change where text is replaced.
        items.push({ q: questionRef(q), markdown: answer, mode: "direct", placement: placementOf.get(q.section_id) });
      }

      report("writing");
      const written = await pullManyIntoDocument(
        bidId,
        items,
        questions.map((x) => x.section_id),
        { styles }
      );
      outcomes.push(...written);
      const synced = written
        .filter((o) => o.status !== "error")
        .map((o) => ({
          sectionId: o.sectionId,
          hash: hashes.get(o.sectionId) ?? "",
          // What a later push is merged from (PE-955, H4).
          base: pulled.get(o.sectionId),
        }));
      report(
        "finishing",
        synced.map((x) => x.sectionId)
      );

      // Also what the document now holds for each, read back from Word, so a
      // later push can tell whether the writer changed it.
      const readBack = synced.length
        ? await readBlocks(
            bidId,
            synced.map((x) => x.sectionId)
          ).catch(() => null)
        : null;
      const withDoc = await Promise.all(
        synced.map(async (x) => {
          const md = readBack?.get(x.sectionId)?.markdown;
          return md ? { ...x, docHash: await contentHash(md) } : x;
        })
      );
      await recordSync(bidId, withDoc, "pull");
      void recountChanged();
      await refreshBlocks();
      return outcomes;
    },
    [bidId, token, questions, refreshBlocks]
  );

  // One pull at a time: two would interleave their writes in Word.
  const pullingRef = useRef<Promise<PullOutcomeRow[]> | null>(null);

  /**
   * Pull questions from mytender into the document (all when ids omitted).
   * A question with no answer in mytender still gets its heading and an
   * empty answer space when it isn't in the document, so pulling into a new
   * document lays out every question; an answer already in the document is
   * never replaced by an empty one.
   */
  const pull = useCallback(
    (
      sectionIds?: string[],
      onProgress?: (p: PullProgress) => void,
      directives?: PullDirectives
    ): Promise<PullOutcomeRow[]> => {
      if (pullingRef.current) return pullingRef.current;
      const run = pullNow(sectionIds, onProgress, directives).finally(() => {
        pullingRef.current = null;
      });
      pullingRef.current = run;
      return run;
    },
    [pullNow]
  );

  /** What a push would do, question by question. */
  const planPush = useCallback(
    async (sectionIds?: string[]): Promise<PushPlan> => {
      if (!bidId || !token) return { rows: [], lastPushAt: null, stale: false };
      const targets = questions.filter((q) => !sectionIds?.length || sectionIds.includes(q.section_id));
      const [contents, live] = await Promise.all([
        readBlocks(bidId),
        fetchLive(
          bidId,
          targets.map((q) => q.section_id),
          token
        ).catch(() => [] as LiveAnswer[]),
      ]);
      const liveBy = new Map(live.map((l) => [l.section_id, l]));
      const record = readSyncRecord(bidId);
      const rows: PushPlanRow[] = [];
      for (const q of targets) {
        const content = contents.get(q.section_id) ?? null;
        const l = liveBy.get(q.section_id) ?? null;
        const remoteHash = l?.answer_hash ?? q.answer_hash;
        const remoteText = l?.answer_markdown ?? q.answer_markdown;
        let verdict: Verdict;
        if (!content) verdict = "missing";
        else if (!content.markdown.trim()) verdict = "skipped";
        else {
          const docHash = await contentHash(content.markdown);
          const last = record.questions[q.section_id];
          // Each side against its own last-synced state; without a record,
          // the best guess is a direct comparison.
          const docChanged = last?.docHash ? docHash !== last.docHash : docHash !== remoteHash;
          const remoteChanged = last ? last.hash !== remoteHash : false;
          if (!remoteText.trim()) verdict = "new";
          else if (!docChanged) verdict = remoteChanged ? "behind" : "unchanged";
          else verdict = remoteChanged ? "newer" : "changed";
        }
        rows.push({ question: q, verdict, content, live: l, include: ["changed", "newer", "new"].includes(verdict) });
      }
      const lastPushAt = record.lastPushAt;
      const newer = rows.filter((r) => r.verdict === "newer" || r.verdict === "behind").length;
      const stale =
        Boolean(lastPushAt) &&
        Date.now() - new Date(lastPushAt as string).getTime() > STALE_DAYS * 86_400_000 &&
        newer >= Math.max(2, Math.ceil(rows.length / 3));
      return { rows, lastPushAt, stale };
    },
    [bidId, token, questions]
  );

  /**
   * Write io.ai's draft into a question's place, as tracked changes. A
   * question whose answer already has text is refused with its paragraph
   * range, so io.ai improves it in place instead of overwriting it.
   */
  const writeAnswer = useCallback(
    async (sectionId: string, markdown: string) => {
      if (!bidId) return { status: "unavailable", message: "Link a tender to write answers." };
      const at = questions.findIndex((x) => x.section_id === sectionId);
      if (at < 0) return { status: "unknown_question", message: "No question with that section_id on this bid." };
      const { blocks: current } = await listBlocks(bidId);
      if ((current.get(sectionId)?.words ?? 0) > 0) {
        const range = (await questionRanges()).find((r) => r.section_id === sectionId);
        return {
          status: "has_text",
          message: "This answer already has text. Improve it with edit_document inside its paragraph range.",
          start: range?.start ?? null,
          end: range?.end ?? null,
        };
      }
      const neighbours = {
        before: questions
          .slice(0, at)
          .reverse()
          .map((x) => x.section_id),
        after: questions.slice(at + 1).map((x) => x.section_id),
      };
      // io.ai's draft is reviewed: always as mytender.io tracked changes.
      const outcome = await pullIntoDocument(bidId, questionRef(questions[at]), markdown, neighbours, "tracked");
      await refreshBlocks();
      return outcome.status === "error"
        ? { status: "error", message: outcome.message }
        : { status: outcome.status, tracked_changes: true };
    },
    [bidId, questions, refreshBlocks]
  );

  /** Push the included rows. */
  const push = useCallback(
    async (rows: PushPlanRow[]): Promise<{ results: PushResult[] }> => {
      if (!bidId || !token) return { results: [] };
      // Read the answers again now: the document may have changed since the
      // sheet was planned (the writer typing, or io.ai finishing the edits the
      // push waited for).
      const included = rows.filter((r) => r.include && r.content);
      const fresh = included.length
        ? await readBlocks(
            bidId,
            included.map((r) => r.question.section_id)
          ).catch(() => null)
        : null;
      const record = readSyncRecord(bidId);
      const payload = included
        .map((r) => {
          const id = r.question.section_id;
          const base = pushBase(
            record.questions[id],
            r.live?.answer_hash ?? r.question.answer_hash,
            r.live?.answer_markdown ?? r.question.answer_markdown
          );
          return {
            section_id: id,
            markdown: fresh?.get(id)?.markdown ?? r.content?.markdown ?? "",
            ...(base !== undefined && { base_markdown: base }),
          };
        })
        .filter((p) => p.markdown.trim());
      let results: PushResult[] = [];
      if (payload.length) {
        try {
          results = await pushQuestions(bidId, payload, token);
        } catch (err) {
          const reason = describeRequestError(err);
          results = payload.map((p) => ({ section_id: p.section_id, status: "failed" as const, reason }));
        }
      }
      const pushedMarkdown = new Map(payload.map((p) => [p.section_id, p.markdown]));
      const synced = await Promise.all(
        results
          .filter((r) => r.status === "pushed" && r.answer_hash)
          .map(async (r) => ({
            sectionId: r.section_id,
            hash: r.answer_hash as string,
            docHash: await contentHash(pushedMarkdown.get(r.section_id) ?? ""),
            base: pushedMarkdown.get(r.section_id),
          }))
      );
      await recordSync(bidId, synced, "push");
      for (const r of results) {
        if (r.status_changed_to) patch(r.section_id, { status: r.status_changed_to });
      }
      void recountChanged();
      void refresh();
      return { results };
    },
    [bidId, token, refresh]
  );

  const changeStatus = useCallback(
    async (sectionId: string, status: SectionStatus) => {
      if (!bidId || !token) return { emailed: null };
      const before = questions.find((q) => q.section_id === sectionId)?.status;
      patch(sectionId, { status });
      return tracked(async () => {
        try {
          return await setQuestionStatus(bidId, sectionId, status, token);
        } catch (err) {
          if (before) patch(sectionId, { status: before });
          throw err;
        }
      });
    },
    [bidId, token, questions]
  );

  const updateWritingPlan = useCallback(
    async (sectionId: string, plan: string) => {
      if (!bidId || !token) return;
      const loaded = questions.find((q) => q.section_id === sectionId)?.writingplan;
      await tracked(() => saveWritingPlan(bidId, sectionId, plan, token, loaded));
      patch(sectionId, { writingplan: plan });
    },
    [bidId, token, questions]
  );

  const sendForReview = useCallback(
    async (sectionId: string, request: ReviewRequest) => {
      if (!bidId || !token) return { emailed: [], skipped: [], reviewer: "" };
      const out = await tracked(() => sendForReviewApi(bidId, sectionId, request, token));
      patch(sectionId, { status: "Awaiting review", reviewer: out.reviewer });
      return out;
    },
    [bidId, token]
  );

  const updateLimit = useCallback(
    async (sectionId: string, value: number | null, type: "words" | "pages" | "characters") => {
      if (!bidId || !token) return;
      const saved = await tracked(() => saveLimit(bidId, sectionId, { limit_value: value, limit_type: type }, token));
      patch(sectionId, saved);
    },
    [bidId, token]
  );

  return {
    questions,
    blocks,
    otherBidId,
    loading,
    error,
    refresh,
    refreshBlocks,
    syncFromServer,
    changedSinceSend,
    recountChanged,
    pull,
    planPush,
    push,
    writeAnswer,
    changeStatus,
    updateWritingPlan,
    updateLimit,
    sendForReview,
    patch,
  };
};

export type QuestionsApi = ReturnType<typeof useQuestions>;
